const defaultStringOperators = {
  equals: 'is',
  'does not equal': "isn't",
  glob: 'matches',
  'is set': 'is set',
  'is not set': 'is blank',
  between: 'between',
  before: 'before',
  after: 'after',
  'include any': 'includes',
  'do not include any': "doesn't include"
}

const comparisonLabels = { gte: 'at least', gt: 'more than', lte: 'at most', lt: 'less than', equals: 'exactly' }
const key = (value) => String(value ?? '')
const readable = (value, fallback = '(please set)') =>
  value === undefined || value === null || value === '' ? fallback : String(value)
const values = (value) =>
  Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : []
const mapLabel = (map, id, fallback = id) => map?.[key(id)] || fallback
const plural = (amount, unit) =>
  amount === 1 ? String(unit).replace(/s$/, '') : String(unit).endsWith('s') ? unit : `${unit}s`
const singular = (word) => String(word).replace(/ies$/, 'y').replace(/es$/, '').replace(/s$/, '')
const bold = (value) => `**${readable(value)}**`

/**
 * @typedef {Object} FlowDescriptionCatalog
 * @property {Record<string, {label?: string, espFeatureSupport?: object}>} sources
 * @property {Record<string, string>} tags
 * @property {Record<string, string>} customFields
 * @property {Record<string, string>} lists
 * @property {Record<string, {label?: string, segments?: Record<string, string>}>} dimensions
 * @property {Record<string, string>} questions
 * @property {Record<string, string>} widgets
 * @property {{tags?: Record<string, string>, categories?: Record<string, string>, pages?: Record<string, string>, posts?: Record<string, string>}} [wordpress]
 */

const operatorAndValue = (operator, value, catalog) => {
  const operators = { ...defaultStringOperators, ...(catalog.stringOperators || {}) }
  if (operator === 'is set' || operator === 'is not set') {
    return operators[operator]
  }
  const alternatives = values(value)
  const formatted = alternatives.length > 1 ? alternatives.map(bold).join(' or ') : bold(alternatives[0] ?? value)
  return `${operators[operator] || operator || 'is'} ${formatted}`
}

const listLabels = (ids, labels) =>
  values(ids)
    .map((id) => mapLabel(labels, id, id))
    .join(' / ') || '(please set)'
const sourceFor = (item, catalog) => catalog.sources?.[key(item.$source)] || {}

export const describePreviousAnswer = (item, catalog = {}) => {
  const question = bold(mapLabel(catalog.questions, item.questionId, 'Question no longer available'))
  if (item.operator === 'has answered' || item.operator === 'has not answered') {
    return `${item.operator} ${question}`
  }
  if (item.operator === 'is empty' || item.operator === 'is not empty') {
    return `${question} answer ${item.operator}`
  }
  if (['is a business email', 'is a personal email', 'has last name', 'has no last name'].includes(item.operator)) {
    return `${question} ${item.operator}`
  }
  if (item.operator === 'in the past' || item.operator === 'in the future') {
    return `${question} answer is ${item.operator}`
  }
  if (item.operator === 'within the next days' || item.operator === 'within the last days') {
    const direction = item.operator === 'within the next days' ? 'next' : 'last'
    return `${question} answer is within the ${direction} ${bold(item.value)} ${item.value === 1 ? 'day' : 'days'}`
  }
  const operators = {
    equals: '=',
    'does not equal': '≠',
    'greater than': '>',
    'greater than or equal to': '≥',
    'less than': '<',
    'less than or equal to': '≤'
  }
  const operator = operators[item.operator] || item.operator
  const formatValue = (value) => {
    const label = ['is', 'is not', 'is any of', 'country is', 'country is not', 'country is any of'].includes(
      item.operator
    )
      ? mapLabel(catalog.countries, value, value)
      : value
    return bold(label === '' ? '""' : label)
  }
  if (item.operator === 'between') {
    const [minimum, maximum] = Array.isArray(item.value) ? item.value : []
    return `${question} answer between ${formatValue(minimum)} and ${formatValue(maximum)}`
  }
  const value = Array.isArray(item.value) ? item.value.map(formatValue).join(' or ') : formatValue(item.value)
  const subject = /^(country |domain |region |city |postal code |first name )/.test(item.operator)
    ? question
    : `${question} answer`
  return `${subject} ${operator} ${value}`
}

export const describeFlowLogicObject = (item, catalog = {}, { preview = false } = {}) => {
  if (!item) {
    return 'No condition specified'
  }
  const source = key(item.$source)
  const type = key(item.$type)
  const provider = sourceFor(item, catalog)
  const providerLabel = provider.label || source
  const capabilities = provider.espFeatureSupport || {}

  if (source === 'answers') {
    return describePreviousAnswer(item, catalog)
  }

  if (catalog.sources?.[source]) {
    if (type === 'isAnonymous') {
      return `Does not exist in ${providerLabel}`
    }
    if (type === 'isSubscriber') {
      return `Exists in ${providerLabel}`
    }
    if (type === 'tags') {
      if (preview) {
        return 'Is tagged'
      }
      const description = capabilities.tag?.conditionDescription || 'tagged'
      const tagNames = listLabels(item.tagIds ?? item.value, catalog.tags)
      return `${item.operator === 'do not include any' ? `not ${description}` : description} in ${providerLabel} ${tagNames}`
    }
    if (type === 'customField') {
      if (preview) {
        return 'Has a custom field value'
      }
      const feature = capabilities.customField || {}
      const field = feature.unstructured
        ? item.customFieldId
        : mapLabel(catalog.customFields, item.customFieldId, feature.create ? item.customFieldId : '(please set)')
      return `${providerLabel} ${(feature.label || 'custom field').toLowerCase()} ${bold(field)} ${operatorAndValue(item.operator, item.value, catalog)}`
    }
    if (type === 'lists') {
      const label = singular(capabilities.list?.label?.toLowerCase() || 'list')
      if (preview) {
        return `Belongs to a ${label}`
      }
      return `${item.operator === 'do not include any' ? "doesn't belong to" : 'belongs to'} ${providerLabel} ${label} ${bold(listLabels(item.listIds ?? item.value, catalog.lists))}`
    }
    if (type === 'eventHistory') {
      const eventLabel = capabilities.eventHistory?.label?.toLowerCase() || 'event'
      if (preview) {
        return `Has fired ${eventLabel}`
      }
      const event = bold(item.eventName)
      if (item.operator === 'has_fired') {
        return `has fired ${providerLabel} ${eventLabel} ${event}`
      }
      if (item.operator === 'has_not_fired') {
        return `has not fired ${providerLabel} ${eventLabel} ${event}`
      }
      if (['gte', 'lt', 'equals'].includes(item.operator)) {
        return `has fired ${providerLabel} ${eventLabel} ${event} ${comparisonLabels[item.operator]} ${bold(item.count || 0)} times`
      }
      return `${providerLabel} ${eventLabel} condition`
    }
    if (type === 'purchaseHistory') {
      const label = capabilities.purchaseHistory?.label?.toLowerCase() || 'purchase'
      if (preview) {
        return `Has made a ${label}`
      }
      if (item.operator === 'is set') {
        return `has purchased anything in ${providerLabel}`
      }
      if (item.operator === 'is not set') {
        return `has not purchased anything in ${providerLabel}`
      }
      const products = values(item.productIds).join(' / ') || '(please set)'
      if (item.operator === 'include any') {
        return `has purchased ${bold(products)} in ${providerLabel}`
      }
      if (item.operator === 'do not include any') {
        return `has not purchased ${bold(products)} in ${providerLabel}`
      }
      return `${providerLabel} ${label} condition`
    }
    if (type === 'totalRevenue') {
      if (preview) {
        return `Total revenue in ${providerLabel}`
      }
      return `total revenue in ${providerLabel} is ${{ gte: 'at least', gt: 'greater than', lte: 'at most', lt: 'less than', equals: 'exactly' }[item.operator] || item.operator} **$${readable(item.value)}**`
    }
  }

  if (source === 'espplaceholder') {
    if (type === 'isAnonymous') {
      return 'visitor is anonymous'
    }
    if (type === 'isSubscriber') {
      return 'visitor exists in email platform'
    }
  }
  if (source === 'wordpress') {
    const [currentOrFavorite, property, taxonomyId] = type.split('-', 3)
    const kind =
      property === 'taxonomy'
        ? taxonomyId === 'post_tag'
          ? 'tags'
          : 'categories'
        : property === 'pageId'
          ? 'pages'
          : property === 'postId'
            ? 'posts'
            : null
    if (kind) {
      const noun = kind === 'categories' ? 'category' : kind.replace(/s$/, '')
      const names = values(item.value)
        .map((entry) => mapLabel(catalog.wordpress?.[kind], entry?.value ?? entry, entry?.value ?? entry))
        .join(' / ')
      return `${currentOrFavorite} WordPress ${noun} ${item.operator === 'do not include any' ? "isn't" : 'is'} ${names}`
    }
  }
  if (source === 'referrer') {
    if (type === 'domain') {
      return `referred by ${values(item.domain).length > 1 ? values(item.domain).map(bold).join(' or ') : bold(item.domain)}`
    }
    if (type === 'direct') {
      return 'direct traffic'
    }
    if (type === 'referral') {
      return 'referred by any site'
    }
    if (type === 'custom') {
      return `referrer ${operatorAndValue(item.operator, item.custom_referrer, catalog)}`
    }
  }
  if (source === 'segments') {
    const dimension = catalog.dimensions?.[type]
    if ((item.operator || '').includes('set')) {
      return `${bold(dimension?.label || type)} ${item.operator === 'is set' ? 'matches any segment' : 'matches no segment'}`
    }
    const names =
      values(item.segmentId)
        .map((id) => mapLabel(dimension?.segments, id, id))
        .join(' / ') || '(please set)'
    return `${bold(dimension?.label || type)} ${item.operator === 'do not include any' ? "isn't" : 'is'} ${bold(names)}`
  }
  if (source === 'rightmessage') {
    if (type === 'widget') {
      return `current widget ${operatorAndValue(
        item.operator,
        values(item.value)
          .map((id) => mapLabel(catalog.widgets, id, id))
          .join(' / '),
        catalog
      )}`
    }
    if (type === 'question') {
      if (item.operator === 'is set') {
        return 'has answered any questions this page view'
      }
      if (item.operator === 'is not set') {
        return "hasn't answered any questions this page view"
      }
      const questions =
        values(item.questionId)
          .map((id) => `"${mapLabel(catalog.questions, id, id)}"`)
          .join(' / ') || '(please set)'
      return `${item.operator === 'do not include any' ? "hasn't answered" : 'has answered'} ${bold(questions)} this page view`
    }
  }
  if (source === 'scores') {
    if (type === 'score') {
      return `score ${bold(item.score)} ${operatorAndValue(item.operator, item.value, catalog)}`
    }
    if (type === 'variable') {
      return `variable ${bold(item.variableName)} ${operatorAndValue(item.operator, item.value, catalog)}`
    }
    if (type === 'inputQuestion') {
      return `input question ${bold(mapLabel(catalog.questions, item.questionId, item.questionId))} ${operatorAndValue(item.operator, item.value, catalog)}`
    }
  }
  if (source === 'location') {
    if (type === 'country') {
      const names = values(item.value)
        .map((id) => mapLabel(catalog.countries, id, id))
        .join(' / ')
      if (names) {
        return `country ${operatorAndValue(item.operator, names, catalog)}`
      }
    }
    if (type === 'city') {
      return `city ${operatorAndValue(item.operator, item.value, catalog)}`
    }
  }
  if (source === 'pageviews') {
    if (type === 'landingpage') {
      return `landing page ${operatorAndValue(item.operator, item.value, catalog)}`
    }
    if (type === 'domain') {
      if (item.operator === 'matches pattern') {
        return item.value === '*' || !String(item.value ?? '').trim()
          ? 'current domain is any domain'
          : `current domain matches ${bold(item.value)}`
      }
      return `current domain ${operatorAndValue(item.operator, item.value, catalog)}`
    }
    if (type === 'path') {
      if (item.occurrence === 'last') {
        if (item.operator === 'matches pattern') {
          return `current page matches pattern ${bold(item.value === '' ? '/' : item.value)}`
        }
        return `current page ${operatorAndValue(item.operator, item.value, catalog)}`
      }
      if (item.occurrence === 'penultimate') {
        return `previous page ${operatorAndValue(item.operator, item.value, catalog)}`
      }
      const page =
        item.operator === 'equals'
          ? readable(item.value)
          : `page that ${operatorAndValue(item.operator, item.value, catalog)}`
      if (item.occurrence === 'not_any') {
        return `${item.timeframe === 'ever' ? 'has never visited' : 'has not visited'} ${page}${item.timeframe === 'within' ? ` in the last ${item.duration || 1} ${plural(item.duration || 1, item.unit || 'day')}` : ''}`
      }
      const count = item.occurrenceCount || 1
      const occurrence = comparisonLabels[item.occurrenceOperator] || 'at least'
      let result = `visited ${page}${item.occurrence === 'any' ? ` ${occurrence} ${count} time${count === 1 ? '' : 's'}` : ''}`
      if (item.timeframe === 'within') {
        result += ` in the last ${item.duration || 1} ${plural(item.duration || 1, item.unit || 'day')}`
      }
      if (item.timeframe === 'outside') {
        result += ` more than ${item.duration || 1} ${plural(item.duration || 1, item.unit || 'day')} ago`
      }
      return result
    }
    if (type === 'newvisitor') {
      return 'first-time visitor'
    }
    if (type === 'returningvisitor') {
      return item.timeframe === 'within'
        ? `has visited in the last ${item.duration} ${plural(item.duration, item.unit)}`
        : item.timeframe === 'outside'
          ? `last visit was more than ${item.duration} ${plural(item.duration, item.unit)} ago`
          : 'has visited before'
    }
    if (type === 'pageCount' || type === 'sessionCount') {
      const count = item.occurrenceCount || 1
      let description =
        type === 'pageCount'
          ? `viewed ${comparisonLabels[item.occurrenceOperator] || 'at least'} ${bold(count)} page${count === 1 ? '' : 's'}`
          : `has visited ${comparisonLabels[item.occurrenceOperator] || 'at least'} ${bold(count)} session${count === 1 ? '' : 's'}`
      if (type === 'pageCount' && item.scope === 'filtered' && item.value) {
        description += ` matching ${bold(item.value)}`
      }
      if (type === 'pageCount' && item.timeframe === 'within') {
        description += ` in the last ${item.duration || 1} ${plural(item.duration || 1, item.unit || 'day')}`
      }
      if (type === 'pageCount' && item.timeframe === 'outside') {
        description += ` more than ${item.duration || 1} ${plural(item.duration || 1, item.unit || 'day')} ago`
      }
      return description
    }
    if (type === 'visitorAge') {
      return `first visited ${comparisonLabels[item.occurrenceOperator] || 'at least'} ${bold(item.duration || 1)} ${plural(item.duration || 1, item.unit || 'day')} ago`
    }
  }
  if (source === 'date') {
    if (type === 'date') {
      return `date ${operatorAndValue(item.operator, item.operator === 'between' ? `${readable(item.value)} and ${readable(item.dateEnd)}` : readable(item.value), catalog)}`
    }
    const names =
      type === 'weekday'
        ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        : type === 'month'
          ? [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December'
            ]
          : null
    if (names) {
      return `${type === 'weekday' ? 'day of the week' : 'Month'} ${operatorAndValue(
        item.operator,
        values(item.value)
          .map((id) => names[Number(id)] || id)
          .join(' / '),
        catalog
      )}`
    }
  }
  if (source === 'utm') {
    return `${type} ${catalog.queryOccurrences?.[item.occurrence] || item.occurrence || ''} ${operatorAndValue(item.operator, item.value, catalog)}`
  }
  if (source === 'query' && type === 'custom') {
    return `query parameter ${bold(item.query)} ${catalog.queryOccurrences?.[item.occurrence] || item.occurrence || ''} ${operatorAndValue(item.operator, item.value, catalog)}`
  }
  if (source === 'device') {
    const subject = type === 'os' ? 'operating system' : type === 'browser' ? 'browser' : type
    if (item.operator === 'is set') {
      return `${subject} is detected`
    }
    if (item.operator === 'is not set') {
      return `${subject} is not detected`
    }
    return `${subject} ${operatorAndValue(item.operator, values(item.value).join(' / '), catalog)}`
  }
  if (source === 'jsdata') {
    const subject =
      type === 'windowVariable'
        ? `javascript **window.${readable(item.variable)}**`
        : type === 'cookie'
          ? `cookie ${bold(item.variable)}`
          : type === 'localStorage'
            ? `localStorage key ${bold(item.variable)}`
            : null
    if (subject) {
      return `${subject} ${operatorAndValue(item.operator, item.value, catalog)}`
    }
  }
  if (source === 'datalayer') {
    return describeDataLayerRule(item, bold, (operator, value) => operatorAndValue(operator, value, catalog))
  }
  return 'No condition specified'
}

const dataLayerComparisons = { gt: 'is more than', lt: 'is less than', gte: 'is at least', lte: 'is at most' }

/** `data layer value **ecommerce.value** is more than **30**`, `data layer event **purchase** fired this session`. */
export const describeDataLayerRule = (item, strong, operatorAndValueText) => {
  if (item.$type === 'event') {
    const key = typeof item.key === 'string' ? item.key.trim() : ''
    const value = typeof item.value === 'string' ? item.value.trim() : item.value
    const match = !key
      ? ''
      : value === undefined || value === null || value === ''
        ? ` with ${strong(key)} set`
        : ` with ${strong(key)} = ${strong(value)}`
    const fired = item.operator === 'has_not_fired' ? 'has not fired' : 'fired'
    return `data layer event ${strong(item.event)}${match} ${fired} ${item.within === 'session' ? 'this session' : 'on this page view'}`
  }
  if (item.$type === 'value') {
    const comparison = dataLayerComparisons[item.operator]
    return `data layer value ${strong(item.variable)} ${comparison ? `${comparison} ${strong(item.value)}` : operatorAndValueText(item.operator, item.value)}`
  }
  return 'No condition specified'
}

const isLogicGroup = (node) => Array.isArray(node?.and) || Array.isArray(node?.or)

/**
 * Normalizes a logic definition into its top-level operator and groups.
 * Top-level `or` holds AND groups (the default); a top-level `and` that holds
 * groups is an AND of OR groups. A flat `and` of rules stays one group.
 *
 * @returns {{ operator: 'and' | 'or', groups: object[] } | null} The operator joining the groups, or null without a definition.
 */
export const flowLogicGroups = (logic) => {
  if (!logic || typeof logic !== 'object') {
    return null
  }
  if (Array.isArray(logic.or)) {
    return { operator: 'or', groups: logic.or }
  }
  if (Array.isArray(logic.and)) {
    return logic.and.some(isLogicGroup) ? { operator: 'and', groups: logic.and } : { operator: 'or', groups: [logic] }
  }
  return { operator: 'or', groups: [{ and: [logic] }] }
}

const describeNode = (node, catalog, options, wrap) => {
  if (node?.not) {
    const inner = describeNode(node.not, catalog, options, false)
    return inner ? `not (${inner})` : ''
  }
  if (!isLogicGroup(node)) {
    return describeFlowLogicObject(node, catalog, options)
  }
  const parts = (node.and || node.or).map((item) => describeNode(item, catalog, options, true)).filter(Boolean)
  const text = parts.join(node.and ? ' and ' : ' or ')
  return wrap && parts.length > 1 ? `(${text})` : text
}

export const describeFlowLogic = (logic, catalog = {}, options = {}) => {
  const normalized = flowLogicGroups(logic)
  if (!normalized || normalized.groups.length === 0) {
    return null
  }
  const groups = normalized.groups.map((group) => describeNode(group, catalog, options, false)).filter(Boolean)
  if (!groups.length) {
    return null
  }
  const description = groups.join(normalized.operator === 'and' ? ', and ' : ', or ')
  return description.charAt(0).toUpperCase() + description.slice(1)
}

export const stripFlowDescriptionMarkup = (description) => String(description || '').replace(/\*\*/g, '')

// Leading words of a rule description that name what the rule checks. They render
// as the muted category; a rule without one starts with its field or value.
const ruleSubject = (item) => {
  const type = key(item?.$type)
  switch (key(item?.$source)) {
    case 'query':
      return 'query parameter'
    case 'referrer':
      return type === 'custom' ? 'referrer' : type === 'direct' ? null : 'referred by'
    case 'pageviews':
      if (type === 'path') {
        return { last: 'current page', penultimate: 'previous page' }[item.occurrence] ?? null
      }
      return { landingpage: 'landing page', domain: 'current domain' }[type] ?? null
    case 'location':
      return type === 'country' || type === 'city' ? type : null
    case 'date':
      return { date: 'date', weekday: 'day of the week', month: 'month' }[type] ?? null
    case 'device':
      return type === 'os' ? 'operating system' : type || null
    case 'jsdata':
      return { windowVariable: 'javascript', cookie: 'cookie', localStorage: 'localStorage key' }[type] ?? null
    case 'datalayer':
      return { value: 'data layer value', event: 'data layer event' }[type] ?? null
    case 'scores':
      return { score: 'score', variable: 'variable', inputQuestion: 'input question' }[type] ?? null
    case 'rightmessage':
      return type === 'widget' ? 'current widget' : null
    default:
      return null
  }
}

// Categories for rules whose description starts directly with the field name.
const displayCategories = { utm: 'UTM parameter' }

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * Splits one rule's description into a muted category and its field/value tokens.
 *
 * @param {object} item The rule (logic leaf).
 * @param {string} description Its `describeFlowLogicObject`/`describeFlowLogic` text with `**bold**` markup.
 * @returns {{ category: string | null, parts: { text: string, kind: 'text' | 'field' | 'value' }[] }} The category label and the remaining tokens.
 */
export const splitFlowRuleDescription = (item, description) => {
  let rest = String(description || '').trim()
  let category = null
  const subject = ruleSubject(item)
  if (subject && rest.toLowerCase().startsWith(`${subject.toLowerCase()} `)) {
    category = rest.slice(0, subject.length)
    rest = rest.slice(subject.length + 1)
  } else if (key(item?.$type) === 'customField' && rest.indexOf('**') > 0) {
    category = rest.slice(0, rest.indexOf('**')).trim()
    rest = rest.slice(rest.indexOf('**'))
  } else if (displayCategories[key(item?.$source)]) {
    category = displayCategories[key(item?.$source)]
    // The description capitalized the field name (e.g. utm_campaign) as its first word.
    rest = rest.charAt(0).toLowerCase() + rest.slice(1)
  }
  const parts = rest
    .split(/(\*\*.*?\*\*)/)
    .map((segment) =>
      segment.startsWith('**') && segment.endsWith('**') && segment.length > 4
        ? { text: segment.slice(2, -2), kind: 'value' }
        : { text: segment.replace(/\*\*/g, '').trim(), kind: 'text' }
    )
    .filter((part) => part.text !== '')
  const [first] = parts
  if (first && first.kind === 'value' && parts.length > 1) {
    first.kind = 'field'
  }
  return { category: category ? capitalize(category) : null, parts }
}
